<script setup lang="ts">
  import {
    type DateType,
    getCalendarSpreadDates,
    addDaysToDate,
    addMonthsToDate,
    getStartOfDay,
    parseDate,
  } from '@travelata-libs/utils';
  import { useInfiniteScroll } from '@vueuse/core';
  import { computed, ref, toRefs, onMounted } from 'vue';
  import { type CalendarModelValue, CalendarSize, type DateTimestamp } from './Calendar.types';
  import CalendarControls from './CalendarControls.vue';
  import CalendarFooter from './CalendarFooter.vue';
  import { useCalendarModel } from './composables/useCalendar';
  import { SCROLL_LOAD_DISTANCE, SCROLL_MONTHS_SIZE } from './utils/constants';
  import { convertModelValueToISO, getScrollMonths } from './utils/date';
  import CalendarTemplate from '@/components/Calendar/CalendarTemplate.vue';
  import CalendarWeek from '@/components/Calendar/CalendarWeek.vue';

  interface CalendarProps {
    modelValue: CalendarModelValue;
    range?: boolean;
    double?: boolean;
    mobile?: boolean;
    scroll?: boolean;
    size?: `${CalendarSize}`;
    minDate?: DateType | null;
    maxDate?: DateType | null;
    flexibleDays?: number | null;
    span?: number | null;
    withFooter?: boolean;
  }

  interface CalendarEmits {
    (e: 'update:modelValue', value: CalendarModelValue): void;
    (e: 'date-click', value: CalendarModelValue): void;
  }

  const props = withDefaults(defineProps<CalendarProps>(), {
    range: false,
    double: false,
    mobile: false,
    scroll: false,
    size: CalendarSize.M,
    minDate: null,
    maxDate: null,
    span: null,
    flexibleDays: null,
    withFooter: true,
  });

  const emit = defineEmits<CalendarEmits>();

  const { range, modelValue } = toRefs(props);
  const additionalDay = ref(0);

  const calendar = useCalendarModel(range, modelValue);
  const displayedDate = computed(() => calendar.displayedDate.value);
  const dates = computed(() => getCalendarSpreadDates(displayedDate.value));
  const extraDisplayedDate = computed(() => addMonthsToDate(calendar.displayedDate.value, 1).getTime());
  const extraDates = computed(() => getCalendarSpreadDates(extraDisplayedDate.value));

  const formattedMinDate = computed(() => (props.minDate ? parseDate(props.minDate) : null));
  const formattedMaxDate = computed(() => (props.maxDate ? parseDate(props.maxDate) : null));

  const isScroll = computed(() => props.mobile && props.double && props.scroll);

  const scrollRef = ref<HTMLDivElement | null>(null);
  const scrollMonthsCount = ref(SCROLL_MONTHS_SIZE);

  const scrollMonths = computed(() =>
    isScroll.value ? getScrollMonths(formattedMinDate.value, formattedMaxDate.value) : [],
  );

  const scrollTemplates = computed(() =>
    scrollMonths.value.slice(0, scrollMonthsCount.value).map((month) => ({
      month,
      dates: getCalendarSpreadDates(month),
    })),
  );

  useInfiniteScroll(
    scrollRef,
    () => {
      scrollMonthsCount.value += SCROLL_MONTHS_SIZE;
    },
    {
      distance: SCROLL_LOAD_DISTANCE,
      canLoadMore: () => scrollMonthsCount.value < scrollMonths.value.length,
    },
  );

  const onClick = (day: DateTimestamp) => {
    calendar.calendarEvents.value.onDayClick?.(day, calendar, props.span);

    const emitValue = convertModelValueToISO(range.value, calendar.systemModelValue.value, additionalDay.value);
    emit('date-click', emitValue);
  };

  const onHover = (day: DateTimestamp) => {
    calendar.calendarEvents.value.onDayHover?.(day, calendar, props.span);
  };

  const onApply = () => {
    const emitValue = convertModelValueToISO(range.value, calendar.systemModelValue.value, additionalDay.value);
    emit('update:modelValue', emitValue);
  };

  const onChangeAdditionalDay = (value: number) => {
    additionalDay.value = value;
  };

  const setDefaultDays = () => {
    const defaultDay = getStartOfDay(Date.now());

    if (props.range) {
      onClick(defaultDay);
      onClick(addDaysToDate(defaultDay, 1).getTime());
    } else {
      onClick(defaultDay);
    }
  };

  onMounted(() => {
    if (modelValue.value) {
      calendar.updateDisplayedDateByModelValue();
    } else {
      setDefaultDays();
    }
  });
</script>

<template>
  <div :class="['tl-calendar', { 'tl-calendar_mobile': mobile, 'tl-calendar_scroll': isScroll }]" tabindex="-1">
    <div class="tl-calendar__header">
      <CalendarControls
        v-if="!isScroll"
        :double="double"
        :mobile="mobile"
        :min-date="formattedMinDate"
        :max-date="formattedMaxDate"
        :size="size"
        data-test-id="calendar-controls"
      />
      <CalendarWeek v-if="mobile" :size="size" class="tl-calendar__week" />
    </div>

    <div
      v-if="isScroll"
      ref="scrollRef"
      class="tl-calendar__inner tl-calendar__inner_scroll"
      data-test-id="calendar-scroll"
    >
      <CalendarTemplate
        v-for="template in scrollTemplates"
        :key="template.month"
        :dates="template.dates"
        :template-date="template.month"
        :flexible-days="flexibleDays"
        :min-date="formattedMinDate"
        :max-date="formattedMaxDate"
        :range="range"
        :mobile="mobile"
        :double="double"
        :scroll="scroll"
        :size="size"
        data-test-id="calendar-template"
        @click="onClick"
        @hover="onHover"
      />
    </div>

    <div v-else class="tl-calendar__inner">
      <CalendarTemplate
        :dates="dates"
        :template-date="displayedDate"
        :flexible-days="flexibleDays"
        :min-date="formattedMinDate"
        :max-date="formattedMaxDate"
        :range="range"
        :mobile="mobile"
        :double="double"
        :size="size"
        data-test-id="calendar-template"
        @click="onClick"
        @hover="onHover"
      />

      <CalendarTemplate
        v-if="double"
        :dates="extraDates"
        :template-date="extraDisplayedDate"
        :min-date="formattedMinDate"
        :flexible-days="flexibleDays"
        :max-date="formattedMaxDate"
        :range="range"
        :mobile="mobile"
        :double="double"
        :size="size"
        data-test-id="double-calendar-template"
        @click="onClick"
        @hover="onHover"
      />
    </div>

    <CalendarFooter
      v-if="withFooter"
      :range="range"
      :mobile="mobile"
      :double="double"
      :size="size"
      data-test-id="calendar-footer"
      @change-additional-day="onChangeAdditionalDay"
      @reset="setDefaultDays"
      @apply="onApply"
    >
      <template #toolbar>
        <slot name="toolbar" />
      </template>
    </CalendarFooter>
  </div>
</template>

<style scoped lang="scss">
  .tl-calendar {
    $root: &;

    background: var(--color-bg-primary);
    position: relative;
    z-index: 1;

    &_mobile {
      #{$root}__header {
        background: var(--color-bg-primary);
        position: sticky;
        top: 0;
        z-index: 5;
      }

      #{$root}__week {
        border-bottom: 1px dashed var(--color-text-tertiary);
        margin-top: var(--spacing-s);
      }

      #{$root}__inner {
        align-items: center;
        flex-direction: column;
      }
    }

    &_scroll {
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0;
    }

    &__inner {
      column-gap: var(--spacing-xxxl);
      display: flex;
      justify-content: center;

      &_scroll {
        flex: 1 1 auto;
        justify-content: flex-start;
        min-height: 0;
        overflow-y: auto;
        overscroll-behavior: contain;
        padding-bottom: 256px;
        scrollbar-width: none;

        &::-webkit-scrollbar {
          display: none;
        }
      }
    }
  }
</style>
